'use client';

import usePlatform from '@haaretz/s-atoms/platform';
import { htzHostnames } from '@haaretz/s-consts';
import breakUrl from '@haaretz/s-url-utils/breakUrl';
import isNextLink from '@haaretz/s-url-utils/isNextLink';
import Link, { useLinkStatus } from 'next/link';
import * as React from 'react';

import { usePageLoaderUpdater } from '@haaretz/s-htz-link/PageLoaderProvider';

import type { LinkProps as NextLinkProps } from 'next/link';

export type Url = NextLinkProps['href'];

/**
 * Extends Next.js's `<Link />` component and takes the modern subset of its
 * props.
 * See the {@link https://nextjs.org/docs/api-reference/next/link | Next.js docs}
 * for more details.
 *
 * @public
 */
export type HtzLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof NextLinkProps
> &
  Omit<
    NextLinkProps,
    // Disallow old NextLink props
    | 'passHref'
    | 'legacyBehavior'
    // NextLink doesn't currently have `children` in its exported props,
    // but this is here to future-proof it in case it is added later.
    | 'children'
    // Conflicts with polymorphic conmonent prop. Redirected from 'hrefAs'
    | 'as'
  > & {
    /** The Children to be rendered inside `<HtzLink>` */
    children?: React.ReactNode;
    /**
     * When different than `href`, the path that will be rendered in the browser
     * URL bar. Used for dynamic routes. Otherwise used to decorate how `href` is
     * displayed in the browser's URL bar.
     */
    hrefAs?: NextLinkProps['as'];
  };

function PageLoaderUpdator() {
  const { pending } = useLinkStatus();

  const { togglePageLoader } = usePageLoaderUpdater();

  React.useEffect(() => {
    togglePageLoader(pending);
  }, [pending, togglePageLoader]);

  return null;
}

export default function HtzLinkComponent({
  ref: forwardedRef,
  children = null,
  href,
  hrefAs,
  locale,
  prefetch = false,
  replace,
  scroll,
  shallow,
  onClick,
  ...attrs
}: HtzLinkProps & React.RefAttributes<HTMLAnchorElement>) {
  const platform = usePlatform();
  const overrideAttrs: React.ComponentProps<'a'> & { 'data-google-interstitial'?: false } = {};
  const { setNextUrl } = usePageLoaderUpdater();

  function onClickHandler(event: React.MouseEvent<HTMLAnchorElement>) {
    if (typeof onClick === 'function') {
      onClick(event);
    }

    if ('onLine' in navigator && !navigator.onLine) {
      event.preventDefault();
      event.stopPropagation();

      setNextUrl(hrefAs?.toString() || href.toString());
    }
  }

  if (typeof href !== 'string' && !href?.href) {
    console.warn(
      'The "href" prop you passed <HtzLink /> is a "URL" object without an "href" field:',
      href,
      '\nOpting out of rendering the element'
    );
    // eslint-disable-next-line no-console
    console.trace();

    return null;
  }

  if (isNextLink(href, platform)) {
    return (
      <Link
        as={hrefAs}
        href={href}
        locale={locale}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        referrerPolicy={hrefAs ? 'unsafe-url' : undefined}
        onClick={onClickHandler}
        {...attrs}
        {...overrideAttrs}
        ref={forwardedRef}
      >
        <PageLoaderUpdator />
        {children}
      </Link>
    );
  }

  const url = typeof href === 'string' ? href : href?.href || undefined;

  const { hostname: urlHostname } = breakUrl(url ?? '');

  const isHtzLink = htzHostnames.some(host => urlHostname.includes(host));

  if (isHtzLink || url?.startsWith('#')) {
    overrideAttrs['data-google-interstitial'] = false;
  }

  return (
    <a href={url} {...attrs} {...overrideAttrs} onClick={onClickHandler} ref={forwardedRef}>
      {children}
    </a>
  );
}
